import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Gauge, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/site/ThemeToggle";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Cuenta creada correctamente");
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1F] flex items-center justify-center p-6" data-testid="register-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle testId="register-theme-toggle" />
      </div>
      <div className="w-full max-w-xl">
        <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F59E0B] text-xs font-mono uppercase tracking-widest mb-8" data-testid="register-back-link">
          <ArrowLeft className="w-3 h-3" /> Volver
        </Link>
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <div className="w-12 h-12 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10">
            <Gauge className="w-6 h-6 text-[#F59E0B]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black uppercase text-lg">SGM México</div>
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Portal de Clientes</div>
          </div>
        </Link>

        <div className="overline mb-3">// Solicite acceso</div>
        <h2 className="font-heading font-black uppercase text-4xl mb-3">Crear Cuenta</h2>
        <p className="text-[#A1A1AA] mb-10">Complete sus datos para acceder al portal de descarga de facturas y documentos técnicos.</p>

        <form onSubmit={submit} className="border border-[#2E2E33] p-8 lg:p-10 bg-[#1F1F25]" data-testid="register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="overline block mb-2">Nombre Completo</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="register-name" />
            </div>
            <div>
              <label className="overline block mb-2">Empresa</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-base" data-testid="register-company" />
            </div>
            <div>
              <label className="overline block mb-2">Email Corporativo</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" data-testid="register-email" />
            </div>
            <div>
              <label className="overline block mb-2">Teléfono</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-base" data-testid="register-phone" />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Contraseña</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-base" data-testid="register-password" />
              <p className="text-[#71717A] text-xs mt-2 font-mono">Mínimo 6 caracteres</p>
            </div>
          </div>
          {error && (
            <div className="mt-5 border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#FCA5A5]" data-testid="register-error">
              {error}
            </div>
          )}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-[#A1A1AA]">
              ¿Ya tiene cuenta?{" "}
              <Link to="/login" className="text-[#F59E0B] hover:underline font-semibold" data-testid="register-to-login">Iniciar Sesión</Link>
            </p>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="register-submit">
              {loading ? "Creando..." : "Crear Cuenta"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
